<script lang="ts">
	import type { ClassValue, HTMLAttributes } from 'svelte/elements';
	import BtnBase from './btnBase.svelte';
	import type { Snippet } from 'svelte';

	type BtnSize = 'mini' | 'little' | 'small' | 'regular' | 'regular-dynamic' | 'large';
	type BtnTheme =
		| 'stone'
		| 'stone/lime-green'
		| 'stone/green'
		| 'stone/green-brown'
		| 'transparent/stone/green'
		| 'transparent/stone/brown/border'
		| 'border/stone/green'
		| 'border/stone/brown'
		| 'border/brown/stone';

	const sizeClasses: Record<BtnSize, string> = {
		mini: 'w-8 h-8 p-1',
		little: 'w-9 h-9 p-3',
		small: 'w-12 h-12 p-2',
		regular: 'w-16 h-16 p-5',
		'regular-dynamic': 'w-10 max-h-10 lg:w-16 lg:max-h-16 p-2 2xl:p-4',
		large: 'w-24 h-24 p-6'
	};

	const themeClasses: Record<BtnTheme, { main: string; fill: string }> = {
		stone: {
			main: 'bg-stone-5 text-brown-2 focus:outline-none',
			fill: 'bg-stone-3'
		},
		'stone/lime-green': {
			main: 'bg-stone-5 text-brown-2 focus:outline-none',
			fill: 'bg-green-5'
		},
		'stone/green': {
			main: 'bg-stone-5 text-green-3 sm:enabled:hover:text-stone-5 transition-colors duration-[350ms] focus:outline-none',
			fill: 'bg-green-3'
		},
		'stone/green-brown': {
			main: 'bg-stone-4 text-brown-2 sm:enabled:hover:text-stone-5 transition-colors duration-[350ms] focus:outline-none btn-shadow',
			fill: 'bg-green-3'
		},
		'transparent/stone/green': {
			main: 'text-stone-5 sm:enabled:hover:text-green-3 transition-colors duration-[350ms] focus:outline-none',
			fill: 'bg-stone-5'
		},
		'transparent/stone/brown/border': {
			main: 'text-brown-2 border border-stone-3 transition-colors duration-[350ms] focus:outline-none',
			fill: 'bg-stone-3'
		},
		'border/stone/green': {
			main: 'bg-stone-5/20 ring-2 ring-stone-5 ring-inset text-stone-5 sm:enabled:hover:text-green-3 transition-colors duration-[350ms] focus:outline-none',
			fill: 'bg-stone-5'
		},
		'border/stone/brown': {
			main: 'bg-stone-5/20 ring-2 ring-stone-5 ring-inset text-stone-5 sm:enabled:hover:text-brown-2 transition-colors duration-[350ms] focus:outline-none',
			fill: 'bg-stone-5'
		},
		'border/brown/stone': {
			main: 'bg-stone-5/20 ring-2 ring-brown-2 ring-inset text-brown-2 sm:enabled:hover:text-stone-5 transition-colors duration-[350ms] focus:outline-none',
			fill: 'bg-brown-2'
		}
	};

	interface Props extends HTMLAttributes<HTMLAnchorElement | HTMLButtonElement> {
		size?: BtnSize;
		theme?: BtnTheme;
		children?: Snippet;
		class?: ClassValue;
		href?: HTMLAnchorElement['href'];
		target?: HTMLAnchorElement['target'];
		rel?: HTMLAnchorElement['rel'];
		disabled?: boolean;
	}

	let {
		size = 'small',
		theme = 'stone',
		children,
		class: className,
		href,
		target,
		rel,
		disabled = false,
		...other
	}: Props = $props();

	let sizeClass = $derived(sizeClasses[size]);
	let themeClass = $derived(themeClasses[theme]);
</script>

<BtnBase
	{...other}
	class={[
		sizeClass,
		themeClass.main,
		'flex aspect-square touch-manipulation items-center justify-center rounded-full',
		...(Array.isArray(className) ? className : [className])
	]}
	fillClass={themeClass.fill}>{@render children?.()}</BtnBase
>

<style>
	:global(.btn-shadow) {
		box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16);
	}
</style>
